import { type ElementType } from 'react';
import {
  LayoutGrid,
  Wallet,
  Wrench,
  CalendarDays,
  Gauge,
  Briefcase,
  TrendingUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export type TechnicianSectionId =
  | 'summary'
  | 'pay'
  | 'skills-coverage'
  | 'schedule'
  | 'capacity'
  | 'jobs'
  | 'performance';

type CountKey = 'jobs' | 'timeOff';

interface NavItem {
  id: TechnicianSectionId;
  label: string;
  icon: ElementType;
  countKey?: CountKey;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const GROUPS: NavGroup[] = [
  {
    items: [
      { id: 'summary', label: 'Summary', icon: LayoutGrid },
      { id: 'pay', label: 'Pay', icon: Wallet },
    ],
  },
  {
    label: 'Work',
    items: [
      { id: 'skills-coverage', label: 'Skills & coverage', icon: Wrench },
      // Schedule & time merges the three "not available" layers (weekly
      // pattern, time off, blocked time) into one section — they're the same
      // thing at three scopes. The badge surfaces pending time-off requests.
      { id: 'schedule', label: 'Schedule & time', icon: CalendarDays, countKey: 'timeOff' },
      { id: 'capacity', label: 'Capacity', icon: Gauge },
    ],
  },
  {
    label: 'Activity',
    items: [
      { id: 'jobs', label: 'Jobs', icon: Briefcase, countKey: 'jobs' },
      { id: 'performance', label: 'Performance', icon: TrendingUp },
    ],
  },
];

export const TECHNICIAN_SECTION_IDS: TechnicianSectionId[] = GROUPS.flatMap((group) =>
  group.items.map((item) => item.id),
);

export function TechnicianSectionNav({
  active,
  onSelect,
  counts,
}: {
  active: TechnicianSectionId;
  onSelect: (id: TechnicianSectionId) => void;
  counts?: Partial<Record<CountKey, number>>;
}) {
  return (
    <nav className="w-[200px] shrink-0 overflow-y-auto border-r bg-muted/30 px-2.5 py-3">
      {GROUPS.map((group, index) => (
        <div key={group.label ?? `group-${index}`}>
          {group.label ? (
            <p className="px-2.5 pb-1.5 pt-3 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground/70">
              {group.label}
            </p>
          ) : null}
          {group.items.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.id;
            const count = item.countKey ? counts?.[item.countKey] : undefined;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item.id)}
                className={cn(
                  'flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-[13px]',
                  isActive
                    ? 'bg-muted font-semibold text-foreground shadow-[inset_2px_0_0_var(--primary)]'
                    : 'font-medium text-muted-foreground hover:bg-muted/70 hover:text-foreground',
                )}
              >
                <Icon
                  className={cn(
                    'size-[15px] shrink-0',
                    isActive ? 'text-foreground' : 'text-muted-foreground',
                  )}
                />
                <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                {typeof count === 'number' && count > 0 ? (
                  <span className="rounded-full bg-muted-foreground/15 px-1.5 text-[11px] font-semibold text-muted-foreground">
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
